import {OEAsset, useClient, useDictionary, useItems, useOrderItem} from '@adamlip/okeoke.client'
import {Button, Card, CardBody} from '@heroui/react'
import {useState, useContext, useEffect} from 'react'
import {AppParamsContext} from 'contexts/AppParamsContext'
import MenuItem from './MenuItem'
import useBasket from 'hooks/useBasket'
import FormattedNumber from 'components/FormattedNumber'
import BasketItemModal from 'views/_Order/BasketModal/Slides/BasketItems/BasketItemModal'
import NumberFlow from '@number-flow/react'
import {Minus, Plus, Trash} from 'lucide-react'

export default function BasketItem({orderItemUUID, orderItems, menuSize}) {
	const {assembledItem, setCount, update} = useOrderItem(null, null, orderItemUUID)
	const {sizes} = useItems()
	const {getValue} = useDictionary()
	const {getName} = useClient()
	const {appParams} = useContext(AppParamsContext)
	const [isOpen, setIsOpen] = useState(false)
	const [showSizeName, setShowSizeName] = useState(true)
	const {order} = useBasket()

	const removeItem = () => {
		order.updateBasketItemCount(orderItemUUID, 0)
		for (let childOrder of orderItems.filter((item) => item.parentOrderItemUUID === orderItemUUID)) {
			order.updateBasketItemCount(childOrder.orderItemUUID, 0)
		}
	}

	const increaseCount = () => {
		if (menuSize) return null
		setCount(assembledItem.count + 1)
		update()
	}

	const decreaseCount = () => {
		if (menuSize) return null
		setCount(assembledItem.count - 1)
		update()
	}

	useEffect(() => {
		if (appParams?.basketModal?.items?.hideSizeName) {
			let haveMultipleItemsFromThis = orderItems.filter((item) => item?.itemUUID === assembledItem?.uuid)?.filter((item) => item?.count > 0)?.length > 1
			let isThisItemHaveMultipleSizes = assembledItem?.visibleSizes > 1
			setShowSizeName(haveMultipleItemsFromThis && isThisItemHaveMultipleSizes ? true : false)
		} else {
			setShowSizeName(true)
		}
	}, [appParams, orderItems, assembledItem])
	if (assembledItem == null) return null
	if (assembledItem?.count === 0) return null

	return (
		<>
			<BasketItemModal isOpen={isOpen} setIsOpen={setIsOpen} orderItemUUID={orderItemUUID} />

			<Card
				className={`flex flex-row shrink-0 gap-2 items-start justify-between rounded-general w-full  bg-orderItemCardBackground text-orderItemCardText border-orderItemBorder border-1`}
				onPress={() => {
					setIsOpen(true)
				}}
				isPressable>
				<CardBody className='flex flex-col gap-0 items-start justify-start w-full'>
					<div className='flex flex-row gap-2 items-start justify-between w-full'>
						<div className=' grow'>
							<div className='flex flex-row gap-2 items-cemter justify-start'>
								{appParams?.items?.showImage && appParams?.assets?.defaultItemImg && (
									<div className={`flex items-center justify-center shrink-0 w-[60px]`}>
										<OEAsset defaultItemImg={appParams?.assets?.defaultItemImg} uuid={assembledItem?.attributes?.appImage} addClass={`w-full h-full ${appParams?.items?.imageRadius || ''} object-contain`} size='l' addContainerClass='w-[100%] h-[100%]' />
									</div>
								)}

								<div className='flex flex-col items-start mb-2'>
									<div className={`${appParams?.styles?.fonts?.capitalize ? 'font-extrabold uppercase' : ''}  font-bold text-md`}>
										{getName(assembledItem.name)}
										{showSizeName && <> ({getName(sizes?.[assembledItem?.selectedSizeUUID]?.name)}) </>} {assembledItem.takeAway === 1 ? 'TA' : ''}
									</div>
									<div className='text-sm font-normal opacity-60'>
										{assembledItem.price > 0 && <FormattedNumber value={assembledItem.price} />}
										{assembledItem.depositPrice > 0 && ` (+${assembledItem.depositPrice} Ft ${getValue('app_v2_item_deposit_price')})`}
									</div>
								</div>
							</div>
							{showSizeName && <div className='basketItem-size font-smallest'>{getName(assembledItem?.sizes[assembledItem.selectedSizeUUID]?.name)}</div>}
						</div>
						<div className='flex flex-row justify-end items-center gap-2'>
							{!menuSize && (
								<>
									<div className='flex flex-row gap-2 w-auto items-center scale-[0.9] p-[0.1rem] bg-background border-1 border-border rounded-full'>
										<Button size='sm' color='primary' className='bg-transparent text-foreground' radius='full' variant='solid' isDisabled={assembledItem?.count < 2} isIconOnly onPress={() => decreaseCount()}>
											<Minus className='w-4 h-4' />
										</Button>
										<div className='min-w-[20px] flex items-center justify-center'>
											<NumberFlow value={+assembledItem?.count || 0} />
										</div>
										<Button size='sm' radius='full' color='primary' className='bg-transparent text-foreground' variant='solid' isIconOnly onPress={() => increaseCount()}>
											<Plus className='w-4 h-4' />
										</Button>
									</div>
									<Button size='sm' radius='full' color='primary' className='bg-transparent text-foreground' variant='solid' isIconOnly onPress={() => removeItem()}>
										<Trash className='w-4 h-4' />
									</Button>
								</>
							)}
						</div>
					</div>
					<div className='w-full flex flex-col gap-0'>
						{(assembledItem?.modifierGroups || [])
							?.filter((group) => group?.modifiers?.some((modifier) => modifier.count > 0))
							.map((group) => {
								if (group?.type === 2 && group?.attributes?.className === 'buttonList') {
									return (
										<div key={group.uuid} className={` mb-2 text-xs ${group.valid ? '' : assembledItem.count > 0 ? 'text-danger' : ''}`}>
											<p className='font-semibold'> {getName(group?.name)}:</p>
											<div className='flex flex-row gap-1'>
												{group.modifiers
													.filter((modifier) => modifier.count > 0)
													.map((modifier, index) => (
														<div key={modifier.uuid} className={`flex flex-row gap-2 items-center justify-between`}>
															<div>
																{`${modifier.count == null || modifier.count === 1 ? '' : modifier.count} ${getName(modifier.name)}`}

																{index < group.modifiers.filter((modifier) => modifier.count > 0).length - 1 && ','}
															</div>

															{+modifier?.price > 0 && (
																<div>
																	<FormattedNumber value={+modifier?.price} />
																</div>
															)}
														</div>
													))}
											</div>
										</div>
									)
								} else {
									return (
										<div key={group.uuid} className={`basketItem-modifierGroup mb-2 text-xs ${group.valid ? 'valid' : assembledItem.count > 0 ? 'invalid' : 'valid'}`}>
											<p className='font-semibold'> {getName(group?.name)}:</p>
											{group.modifiers
												.filter((modifier) => modifier.count > 0)
												.map((modifier) => (
													<div key={modifier.uuid} className={`w-full flex flex-row gap-2 items-center justify-between`}>
														<div>{`${modifier.count == null || modifier.count === 1 ? '' : modifier.count} ${getName(modifier.name)}`}</div>
														<div>
															<FormattedNumber value={modifier?.price} />
														</div>
													</div>
												))}
										</div>
									)
								}
							})}
						<div className='basketItem-modifier'>{assembledItem.notes}</div>
						<div className=''>
							{orderItems
								.filter((item) => item.parentOrderItemUUID === orderItemUUID)
								.map((item) => (
									<MenuItem key={item.orderItemUUID} item={item} />
								))}
						</div>
					</div>
				</CardBody>
			</Card>
		</>
	)
}
